import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, RotateCcw, Copy, PlugZap, CheckCircle2, AlertTriangle, Trash2, FileCode2, Download } from "lucide-react";
import { useFileViewer } from "@/components/FileViewer";

const copy = (text) => {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Copied to the clipboard"),
    () => toast.error("Could not copy — select the text instead"));
};

const Mono = ({ text, testid, rows = 12 }) => (
  <pre data-testid={testid}
       className="max-h-[420px] overflow-auto border border-slate-200 bg-slate-950 p-3 font-mono text-[12px] leading-relaxed text-teal-100"
       style={{ minHeight: `${rows * 1.1}rem` }}>
    {text}
  </pre>
);

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-0 border-b border-slate-100 py-1 text-sm sm:flex-row sm:gap-2">
    <span className="shrink-0 text-xs uppercase tracking-wide text-slate-500 sm:w-52">{label}</span>
    <span className="min-w-0 break-words text-slate-800">{value || <span className="text-slate-400">—</span>}</span>
  </div>
);

export default function Interop() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const wanted = params.get("ehr");
  const [meta, setMeta] = useState(null);
  const [spec, setSpec] = useState(null);
  const { openFile, viewerElement } = useFileViewer();
  const [vendor, setVendor] = useState(wanted === "eclinicalworks" ? "eclinicalworks" : "meditech");
  const [hl7, setHl7] = useState("");
  const [fhir, setFhir] = useState("");
  const [result, setResult] = useState(null);
  const [fhirResult, setFhirResult] = useState(null);
  const [outbound, setOutbound] = useState(null);
  const [log, setLog] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState("");

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await api.get("/interop/health");
      setHealth(data);
    } catch (e) { /* strip is non-critical */ }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get("/interop/messages", { params: { limit: 60 } });
      setLog(data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    api.get("/interop/meta").then((r) => {
      setMeta(r.data);
      const key = wanted === "eclinicalworks" ? "eclinicalworks" : "meditech";
      setHl7(r.data.samples[key]);
      setFhir(JSON.stringify(r.data.fhir_samples[key], null, 2));
    }).catch((e) => toast.error(apiError(e)));
    api.get("/interop/spec").then((r) => setSpec(r.data)).catch(() => {});
    loadLog();
    loadHealth();
  }, [loadLog, loadHealth]);

  const resetSample = (key = vendor) => {
    if (!meta) return;
    setHl7(meta.samples[key]);
    setFhir(JSON.stringify(meta.fhir_samples[key], null, 2));
    setResult(null);
    setFhirResult(null);
  };

  const switchVendor = (key) => {
    setVendor(key);
    if (meta) {
      setHl7(meta.samples[key]);
      setFhir(JSON.stringify(meta.fhir_samples[key], null, 2));
    }
    setResult(null);
    setFhirResult(null);
    setOutbound(null);
  };

  const sendOrder = async () => {
    setBusy("order");
    try {
      const { data } = await api.post("/interop/hl7/orders", { message: hl7, vendor });
      setResult(data);
      setOutbound(null);
      if (data.accepted && !data.duplicate) toast.success(`Accessioned as ${data.case.accession_number}`);
      else if (data.duplicate) toast.info(`Duplicate order — already ${data.case.accession_number}`);
      else toast.error("Order rejected — see the validation list");
      loadLog();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const validate = async () => {
    setBusy("validate");
    try {
      const { data } = await api.post("/interop/hl7/parse", { message: hl7, vendor });
      setResult({ ...data, accepted: null, order: data.order, problems: data.problems, preview: true });
      toast[data.ok ? "success" : "error"](data.ok ? "Message parses cleanly" : "Validation problems found");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const sendFhir = async () => {
    setBusy("fhir");
    try {
      const resource = JSON.parse(fhir);
      const { data } = await api.post("/interop/fhir/ServiceRequest", { resource, vendor });
      setFhirResult(data);
      toast.success(data.duplicate
        ? `Duplicate — already ${data.case.accession_number}`
        : `Accessioned as ${data.case.accession_number}`);
      loadLog();
    } catch (e) {
      toast.error(e instanceof SyntaxError ? "That is not valid JSON" : apiError(e));
    } finally { setBusy(""); }
  };

  const buildResult = async (caseId, kind) => {
    setBusy("result");
    try {
      const path = kind === "fhir"
        ? `/interop/fhir/DiagnosticReport/${caseId}`
        : `/interop/hl7/results/${caseId}`;
      const { data } = await api.get(path, { params: { vendor, send: true } });
      setOutbound({
        kind,
        text: kind === "fhir" ? JSON.stringify(data.resource, null, 2) : data.message,
        final: data.final,
        accession: data.accession_number,
      });
      toast.success(`${kind === "fhir" ? "DiagnosticReport" : "ORU^R01"} sent to ${meta.vendors.find((v) => v.key === vendor).label}`);
      loadLog();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!meta) return <div className="text-sm text-slate-500" data-testid="interop-loading">Loading interop simulator…</div>;
  const v = meta.vendors.find((x) => x.key === vendor);
  const activeCase = result?.case || fhirResult?.case;
  const canClear = ["admin", "supervisor"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="interop-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">EHR interoperability</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <PlugZap size={26} className="text-[#134e4a]" /> Meditech & eClinicalWorks simulator
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            A live demo of the order-to-result loop for your IT department: send a real HL7 v2 order message
            (or FHIR R4 ServiceRequest), watch PathQity accession it and return an ACK, then generate the
            ORU^R01 result or DiagnosticReport that goes back to the EHR.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => resetSample()} data-testid="reset-sample-button" className="h-11 border-slate-300">
            <RotateCcw size={15} className="mr-2" /> Reset sample
          </Button>
        </div>
      </div>

      {health && (
        <section data-testid="interop-health-strip"
                 className={`flex flex-wrap items-center gap-x-4 gap-y-1 border-l-4 px-3 py-2 text-sm ${
                   health.status === "red" ? "border-red-700 bg-red-50 text-red-900"
                     : health.status === "amber" ? "border-amber-500 bg-amber-50 text-amber-900"
                     : "border-emerald-600 bg-emerald-50 text-emerald-900"}`}>
          {health.status === "green"
            ? <CheckCircle2 size={16} className="shrink-0" />
            : <AlertTriangle size={16} className="shrink-0" />}
          <span className="font-semibold" data-testid="interop-health-status">
            {health.status === "red" ? "Interface may be down"
              : health.status === "amber" ? "Reports waiting to be pulled" : "Interface healthy"}
          </span>
          <span data-testid="interop-health-outbound">
            Last result retrieved {health.last_result_hours === null ? "never" : `${health.last_result_hours} h ago`}
          </span>
          <span data-testid="interop-health-inbound">
            Last order received {health.last_inbound_hours === null ? "never" : `${health.last_inbound_hours} h ago`}
          </span>
          <span data-testid="interop-health-pending">{health.pending_releases} report(s) awaiting release</span>
          <span className="text-xs opacity-80">
            Watchdog alerts after {health.threshold_hours} h of silence, {health.lab_hours} · lab time {health.lab_time}
          </span>
          {canClear && (
            <Button size="sm" variant="outline" data-testid="run-watchdog-button" className="ml-auto h-8 border-slate-300 bg-white"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/interop/watchdog/run");
                        toast.success(`Watchdog ran — ${data.outcome.replace(/_/g, " ")}`);
                        loadHealth();
                      } catch (e) { toast.error(apiError(e)); }
                    }}>
              Run watchdog now
            </Button>
          )}
        </section>
      )}

      <div className="flex flex-wrap gap-2" data-testid="vendor-switch">        {meta.vendors.map((x) => (
          <button key={x.key} onClick={() => switchVendor(x.key)} data-testid={`vendor-${x.key}`}
                  className={`border px-4 py-2 text-sm font-semibold transition-colors ${
                    vendor === x.key
                      ? "border-[#134e4a] bg-[#134e4a] text-white"
                      : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {x.label}
          </button>
        ))}
      </div>

      <section className="grid gap-3 border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[2fr_1fr]" data-testid="vendor-profile">
        <div>
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">{v.label} connection profile</h2>
          <p className="mt-1 text-sm text-slate-600">{v.notes}</p>
        </div>
        <dl className="space-y-1 text-sm">
          <Row label="Sending application" value={v.sending_app} />
          <Row label="Sending facility" value={v.sending_facility} />
          <Row label="Order message" value={v.order_type} />
          <Row label="HL7 version" value={v.hl7_version} />
          <Row label="Receiving application" value={meta.receiving_application} />
          <Row label="Receiving facility" value={meta.receiving_facility} />
        </dl>
      </section>

      <Tabs defaultValue={wanted === "fhir" ? "fhir" : "hl7"}>
        <TabsList data-testid="interop-tabs">
          <TabsTrigger value="hl7" data-testid="tab-hl7">HL7 v2 orders</TabsTrigger>
          <TabsTrigger value="fhir" data-testid="tab-fhir">FHIR R4</TabsTrigger>
          <TabsTrigger value="endpoints" data-testid="tab-endpoints">Endpoints for IT</TabsTrigger>
        </TabsList>

        <TabsContent value="hl7" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                Inbound order message ({v.order_type})
              </Label>
              <Textarea value={hl7} onChange={(e) => setHl7(e.target.value)} data-testid="hl7-input"
                        spellCheck={false}
                        className="mt-1 min-h-[260px] border-slate-300 bg-slate-950 font-mono text-[12px] text-teal-100" />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button onClick={sendOrder} disabled={busy === "order"} data-testid="send-order-button"
                        className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Send size={15} className="mr-2" />
                  {busy === "order" ? "Sending…" : "Send order to PathQity"}
                </Button>
                <Button variant="outline" onClick={validate} disabled={busy === "validate"}
                        data-testid="validate-button" className="h-11 border-slate-300">
                  Validate only
                </Button>
                <Button variant="outline" onClick={() => copy(hl7)} data-testid="copy-hl7-button" className="h-11 border-slate-300">
                  <Copy size={15} />
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {result?.problems?.length > 0 && (
                <div className="border-l-2 border-red-700 bg-red-50 p-3" data-testid="interop-problems">
                  <p className="flex items-center gap-2 text-sm font-semibold text-red-900">
                    <AlertTriangle size={15} /> Validation problems
                  </p>
                  <ul className="mt-1 space-y-1 text-sm text-red-900">
                    {result.problems.map((p) => <li key={p}>· {p}</li>)}
                  </ul>
                </div>
              )}
              {result?.order && (
                <div className="border border-slate-200 p-3" data-testid="parsed-order">
                  <h3 className="text-sm font-semibold text-slate-900">Parsed order</h3>
                  <div className="mt-2">
                    <Row label="Message type" value={result.order.message_type} />
                    <Row label="Control ID" value={result.order.message_control_id} />
                    <Row label="Placer order number" value={result.order.placer_order_number} />
                    <Row label="Patient" value={result.order.patient_name} />
                    <Row label="MRN" value={result.order.patient_mrn} />
                    <Row label="DOB / sex" value={`${result.order.patient_dob || "—"} · ${result.order.patient_sex || "—"}`} />
                    <Row label="Ordering physician" value={result.order.ordering_physician} />
                    <Row label="Service" value={`${result.order.service_code} — ${result.order.service_text}`} />
                    <Row label="Specimen site" value={result.order.specimen_site || result.order.specimen_source} />
                    <Row label="Priority" value={result.order.priority} />
                    <Row label="Clinical notes" value={result.order.clinical_notes} />
                  </div>
                </div>
              )}
              {result?.case && (
                <div className="border-l-2 border-[#134e4a] bg-teal-50 p-3" data-testid="interop-case-created">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#134e4a]">
                    <CheckCircle2 size={15} />
                    {result.duplicate ? "Duplicate order — matched existing case" : "Case accessioned"}
                  </p>
                  <Link to={`/cases/${result.case.id}`} data-testid="interop-case-link"
                        className="mt-1 inline-block font-mono text-lg font-bold text-[#134e4a] hover:underline">
                    {result.case.accession_number}
                  </Link>
                  <p className="text-sm text-slate-700">
                    {result.case.patient_name} · {result.case.specimen_type} · {result.case.priority}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={busy === "result"}
                            onClick={() => buildResult(result.case.id, "hl7")}
                            data-testid="build-oru-button" className="h-10 border-slate-300">
                      Send ORU^R01 result back
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === "result"}
                            onClick={() => buildResult(result.case.id, "fhir")}
                            data-testid="build-fhir-result-button" className="h-10 border-slate-300">
                      Send FHIR DiagnosticReport
                    </Button>
                  </div>
                </div>
              )}
              {result?.ack && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">
                    ACK returned to {v.label}
                  </Label>
                  <Mono text={result.ack.replace(/\r/g, "\n")} testid="ack-output" rows={4} />
                </div>
              )}
            </div>
          </div>

          {outbound && (
            <div data-testid="outbound-result">
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                Outbound {outbound.kind === "fhir" ? "DiagnosticReport" : "ORU^R01"} for {outbound.accession} —{" "}
                {outbound.final ? "final result" : "preliminary (case not signed out yet)"}
              </Label>
              <Mono text={outbound.text.replace(/\r/g, "\n")} testid="outbound-message" />
              <Button variant="outline" onClick={() => copy(outbound.text)} data-testid="copy-outbound-button"
                      className="mt-2 h-11 border-slate-300">
                <Copy size={15} className="mr-2" /> Copy message
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="fhir" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">
                FHIR R4 ServiceRequest (POST /api/interop/fhir/ServiceRequest)
              </Label>
              <Textarea value={fhir} onChange={(e) => setFhir(e.target.value)} data-testid="fhir-input"
                        spellCheck={false}
                        className="mt-1 min-h-[320px] border-slate-300 bg-slate-950 font-mono text-[12px] text-teal-100" />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button onClick={sendFhir} disabled={busy === "fhir"} data-testid="send-fhir-button"
                        className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Send size={15} className="mr-2" />
                  {busy === "fhir" ? "Sending…" : "Send ServiceRequest"}
                </Button>
                <Button variant="outline" onClick={() => copy(fhir)} data-testid="copy-fhir-button" className="h-11 border-slate-300">
                  <Copy size={15} />
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {fhirResult?.case && (
                <div className="border-l-2 border-[#134e4a] bg-teal-50 p-3" data-testid="fhir-case-created">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#134e4a]">
                    <CheckCircle2 size={15} />
                    {fhirResult.duplicate ? "Duplicate — matched existing case" : "Case accessioned from FHIR"}
                  </p>
                  <Link to={`/cases/${fhirResult.case.id}`} data-testid="fhir-case-link"
                        className="mt-1 inline-block font-mono text-lg font-bold text-[#134e4a] hover:underline">
                    {fhirResult.case.accession_number}
                  </Link>
                  <p className="text-sm text-slate-700">
                    {fhirResult.case.patient_name} · {fhirResult.case.specimen_type} · {fhirResult.case.priority}
                  </p>
                  <Button size="sm" variant="outline" disabled={busy === "result"}
                          onClick={() => buildResult(fhirResult.case.id, "fhir")}
                          data-testid="fhir-build-report-button" className="mt-2 h-10 border-slate-300">
                    Send DiagnosticReport back
                  </Button>
                </div>
              )}
              <p className="text-sm text-slate-600">
                eClinicalWorks can pull results as a FHIR <code>DiagnosticReport</code> instead of HL7.
                The report carries the diagnosis as <code>conclusion</code> and the full narrative as a
                base64 <code>presentedForm</code>.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="endpoints" className="mt-4 space-y-4">
          <div className="border border-slate-200 p-4" data-testid="interop-endpoints">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
                  <FileCode2 size={16} className="text-[#134e4a]" /> Hand this to your interface analyst
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  All endpoints are authenticated (bearer token or session cookie) and live under the same host as this app.
                  MLLP framing is handled by your integration engine / VPN tunnel; PathQity accepts the message body over HTTPS.
                </p>
              </div>
              <Button onClick={() => openFile("/interop/spec.pdf", {
                        filename: "PathQity_EHR_Interface_Specification.pdf",
                        title: "EHR Interface Specification — Meditech & eClinicalWorks",
                      })}
                      data-testid="download-interface-spec-button"
                      className="ml-auto h-11 bg-[#134e4a] hover:bg-[#0f3e3a]">
                <Download size={15} className="mr-2" /> Download IT interface spec (PDF)
              </Button>
            </div>
            <div className="mt-3 space-y-1">
              {Object.entries(meta.endpoints).map(([k, path]) => (
                <Row key={k} label={k.replace(/_/g, " ")} value={path} />
              ))}
              <Row label="Segments consumed" value="MSH, PID, PV1, ORC, OBR, NTE" />
              <Row label="ACK codes returned" value={Object.entries(meta.ack_codes).map(([c, t]) => `${c} = ${t}`).join(" · ")} />
              <Row label="Result segments sent" value="MSH, PID, OBR, OBX (LOINC 22637-3 / 22638-1 / 22636-5), NTE on amendments" />
              <Row label="Duplicate handling" value="Matched on ORC-2 placer order number — a repeat order returns AA and the original accession" />
            </div>
          </div>

          {spec && (
            <div className="border border-slate-200" data-testid="interface-spec-onscreen">
              <header className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <h2 className="text-base font-semibold text-slate-900 md:text-lg">{spec.title}</h2>
                <p className="text-sm text-slate-600">
                  {spec.subtitle} · v{spec.document_version} · generated {spec.generated_at}
                </p>
                <p className="mt-1 text-xs text-slate-500">{spec.notice}</p>
              </header>
              <div className="divide-y divide-slate-100">
                {spec.sections.map((s) => (
                  <section key={s.title} className="px-4 py-4" data-testid={`spec-section-${s.title.split(".")[0]}`}>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#134e4a]">{s.title}</h3>
                    {s.intro && <p className="mt-1 text-sm text-slate-700">{s.intro}</p>}
                    {s.bullets.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                        {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    )}
                    {s.tables.map((t) => (
                      <div key={t.label} className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t.label}</p>
                        <div className="mt-1 overflow-x-auto">
                          <table className="w-full min-w-[520px] border border-slate-200 text-xs">
                            <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                              <tr>{t.headers.map((h) => <th key={h} className="border-b border-slate-200 px-2 py-1.5">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {t.rows.map((r, i) => (
                                <tr key={i} className="border-b border-slate-100 align-top">
                                  {r.map((c, j) => <td key={j} className="px-2 py-1.5 text-slate-700">{c || "—"}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    {s.code.map((c) => (
                      <div key={c.label} className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.label}</p>
                        <pre className="mt-1 overflow-x-auto bg-slate-900 p-3 text-[11px] leading-relaxed text-teal-100">{c.text}</pre>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <section className="border border-slate-200" data-testid="interop-log">
        <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Message log ({log.length})</h2>
          {canClear && log.length > 0 && (
            <Button size="sm" variant="outline" data-testid="clear-log-button" className="ml-auto h-9 border-slate-300"
                    onClick={async () => {
                      try {
                        await api.delete("/interop/messages");
                        toast.success("Simulator log cleared");
                        loadLog();
                      } catch (e) { toast.error(apiError(e)); }
                    }}>
              <Trash2 size={14} className="mr-1" /> Clear log
            </Button>
          )}
        </header>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Direction</th>
                <th className="px-3 py-2">Vendor</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Detail</th>
                <th className="px-3 py-2">Case</th>
              </tr>
            </thead>
            <tbody>
              {log.map((m) => (
                <tr key={m.id} className="border-b border-slate-100" data-testid={`log-row-${m.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDateTime(m.at)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${
                      m.direction === "inbound" ? "bg-sky-100 text-sky-900" : "bg-teal-100 text-teal-900"}`}>
                      {m.direction}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{m.vendor_label}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{m.kind}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs font-semibold ${
                      m.status === "rejected" ? "text-red-800" : m.status === "duplicate" ? "text-amber-800" : "text-green-800"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="max-w-[320px] px-3 py-2 text-xs text-slate-600">{m.detail}</td>
                  <td className="px-3 py-2">
                    {m.case_id
                      ? <Link to={`/cases/${m.case_id}`} className="font-mono text-xs font-semibold text-[#134e4a] hover:underline">{m.accession_number}</Link>
                      : <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
              {log.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500" data-testid="interop-log-empty">
                  No messages yet — send the sample order above.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {activeCase && (
        <p className="text-sm text-slate-600" data-testid="interop-followup-hint">
          Tip for the demo: open {activeCase.accession_number}, gross it, sign it out, then come back and press
          "Send ORU^R01 result back" — the message flips from preliminary (P) to final (F) with the diagnosis in OBX.
        </p>
      )}
      {viewerElement}
    </div>
  );
}
